import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckboxField, FormActions, FormGrid, NumberField, SelectField, TextField, useForm,
} from '../../components/Form';
import { PhotoCapture } from '../../components/PhotoCapture';
import { api } from '../../lib/api';
import { useMutation, useResource } from '../../lib/hooks';

type Lookup = { id: number; description: string };

/**
 * Blank optional text becomes null.
 *
 * Several entity properties pair StringLength(n, MinimumLength = n) with a RegularExpression
 * but are NOT [Required]. An empty string violates both; null satisfies both. Sending "" for
 * a field the user chose to leave empty is what produced "One or more validation errors
 * occurred" on an otherwise complete form.
 */
const orNull = (value: string | null | undefined) => {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
};

/**
 * The phone formats the Student entity enforces.
 *
 * Anchored, because DataAnnotations' RegularExpression requires a full match - an unanchored
 * copy here would accept values the server then rejects, which is worse than not checking.
 * These exist to catch the mistake before the round trip; the server remains the authority.
 */
const MOBILE_PATTERN = /^92-3[0-9]{9}$/;
const LANDLINE_PATTERN = /^0[0-9]{2}-[0-9]{8}$/;
const CNIC_PATTERN = /^[0-9]{5}-[0-9]{7}-[0-9]$/;

/**
 * The Name / Address / Mobile / CNIC / Email rules the Teacher and Staff entities carry,
 * annotation for annotation - both are [Required], Mobile and CNIC add a fixed-length
 * [RegularExpression], Email adds [EmailAddress] and [StringLength(50)]. The legacy forms
 * flagged all of these inline through jQuery-unobtrusive; this reproduces that before the
 * round trip. The server stays the authority.
 */
function validatePerson(v: {
  name: string; address: string; mobileNo: string; cnic: string; email: string;
}): Record<string, string> {
  const e: Record<string, string> = {};

  if (!v.name.trim()) e.name = 'Name is required.';
  else if (v.name.trim().length > 50) e.name = 'Name cannot exceed 50 characters.';

  if (!v.address.trim()) e.address = 'Address is required.';

  if (!v.mobileNo.trim()) e.mobileNo = 'Mobile No. is required.';
  else if (!MOBILE_PATTERN.test(v.mobileNo.trim())) {
    e.mobileNo = 'Must be in the format 92-3######### (13 characters).';
  }

  if (!v.cnic.trim()) e.cnic = 'CNIC is required.';
  else if (!CNIC_PATTERN.test(v.cnic.trim())) {
    e.cnic = 'Must be in the format #####-#######-# (15 characters).';
  }

  if (!v.email.trim()) e.email = 'Email is required.';
  else if (!v.email.includes('@')) e.email = 'Enter a valid email address.';
  else if (v.email.trim().length > 50) e.email = 'Email cannot exceed 50 characters.';

  return e;
}

/** Marks the offending fields, then puts the cursor in the first of them. */
function showErrors(
  setErrors: (errors: Record<string, string>) => void,
  errors: Record<string, string>,
) {
  setErrors(errors);
  const first = Object.keys(errors)[0];
  if (first) document.getElementById(first)?.focus();
}

/** Shared page chrome for a create/edit form. */
function FormShell({
  title, subtitle, loading, error, children,
}: {
  title: string;
  subtitle?: string;
  loading?: boolean;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">{title}</h2>
          {subtitle && <p className="page__subtitle">{subtitle}</p>}
        </div>
      </div>

      <div className="card">
        {loading && <p>Loading…</p>}
        {error && <p className="field__error">{error}</p>}
        {!loading && !error && children}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Student
// ---------------------------------------------------------------------------

type StudentValues = {
  id: number;
  name: string;
  fatherName: string;
  schoolName: string;
  schoolBranch: string;
  studentMobileNo: string;
  parentMobileNo: string;
  homeLandline: string;
  studentEmail: string;
  parentEmail: string;
  gradeId: number | '';
  isReportToStudent: boolean;
  isReportToParent: boolean;
  isSendFeesReportToStudent: boolean;
  isSendFeesReportToParent: boolean;
};

const emptyStudent: StudentValues = {
  id: 0, name: '', fatherName: '', schoolName: '', schoolBranch: '',
  studentMobileNo: '', parentMobileNo: '', homeLandline: '',
  studentEmail: '', parentEmail: '', gradeId: '',
  // New students opt in to every notification by default. On edit this object is only the
  // merge base - the student's saved flags come back from GetById and override these.
  isReportToStudent: true, isReportToParent: true,
  isSendFeesReportToStudent: true, isSendFeesReportToParent: true,
};

/**
 * Student create / edit.
 *
 * On create the registration fee is sent alongside the student. The server writes that
 * amount verbatim - it does not re-read master data - so the field is editable and
 * pre-filled with the current default, exactly as the legacy form behaved.
 * See KnowledgeBase/findings/posted-amount-trust.md.
 */
export function StudentFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const formData = useResource<{ grades: Lookup[]; registrationFeesAmount: number }>(
    '/api/students/form-data');
  const existing = useResource<StudentValues>(isEdit ? `/api/students/${id}` : null);

  const form = useForm<StudentValues>(emptyStudent);
  const [registrationFees, setRegistrationFees] = useState('');
  const [base64Image, setBase64Image] = useState('');

  useEffect(() => {
    if (existing.data) form.setValues({ ...emptyStudent, ...existing.data });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data]);

  useEffect(() => {
    if (formData.data && !isEdit) setRegistrationFees(String(formData.data.registrationFeesAmount));
  }, [formData.data, isEdit]);

  const v = form.values;

  /**
   * Mirrors the entity's own rules so a mistake is reported against the field that has it,
   * before a round trip. Optional fields are only checked once they hold something - blank
   * means "no rule applies", which is exactly how they are sent.
   */
  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!v.name.trim()) errors.name = 'Student name is required.';
    else if (v.name.trim().length > 50) errors.name = 'Student name cannot exceed 50 characters.';

    if (!v.fatherName.trim()) errors.fatherName = 'Father name is required.';
    else if (v.fatherName.trim().length > 50) errors.fatherName = 'Father name cannot exceed 50 characters.';

    if (!v.gradeId) errors.gradeId = 'Select a grade.';

    const mobile = orNull(v.studentMobileNo);
    if (mobile && !MOBILE_PATTERN.test(mobile)) {
      errors.studentMobileNo = 'Must be in the format 92-3######### (13 characters).';
    }

    const parentMobile = orNull(v.parentMobileNo);
    if (parentMobile && !MOBILE_PATTERN.test(parentMobile)) {
      errors.parentMobileNo = 'Must be in the format 92-3######### (13 characters).';
    }

    const landline = orNull(v.homeLandline);
    if (landline && !LANDLINE_PATTERN.test(landline)) {
      errors.homeLandline = 'Must be in the format 0##-######## (12 characters).';
    }

    const studentEmail = orNull(v.studentEmail);
    if (studentEmail && !studentEmail.includes('@')) errors.studentEmail = 'Enter a valid email address.';
    else if (studentEmail && studentEmail.length > 60) errors.studentEmail = 'Email cannot exceed 60 characters.';

    const parentEmail = orNull(v.parentEmail);
    if (parentEmail && !parentEmail.includes('@')) errors.parentEmail = 'Enter a valid email address.';
    else if (parentEmail && parentEmail.length > 60) errors.parentEmail = 'Email cannot exceed 60 characters.';

    return errors;
  }

  async function save() {
    const problems = validate();
    if (Object.keys(problems).length > 0) {
      // Focus moves to the first bad field: on a form this tall the message can be off
      // screen, and a Save that appears to do nothing is the worst version of this.
      showErrors(form.setErrors, problems);
      return;
    }

    form.setErrors({});

    const payload = {
      student: {
        ...v,
        gradeId: Number(v.gradeId) || 0,

        // Optional text has to go up as null, never "".
        //
        // The entity carries StringLength(13, MinimumLength = 13) and a RegularExpression on
        // each phone number. An empty string is length 0, so it fails BOTH - and because
        // [ApiController] validates the model before the action runs, the whole save was
        // rejected with "One or more validation errors occurred" for fields the user had
        // deliberately left blank. Null skips those rules, which is what "optional" means.
        schoolName: orNull(v.schoolName),
        schoolBranch: orNull(v.schoolBranch),
        studentMobileNo: orNull(v.studentMobileNo),
        parentMobileNo: orNull(v.parentMobileNo),
        homeLandline: orNull(v.homeLandline),
        studentEmail: orNull(v.studentEmail),
        parentEmail: orNull(v.parentEmail),
      },
      base64Image: base64Image || null,
      registrationFeesAmount: Number(registrationFees) || 0,
    };

    const ok = await run(
      () => (isEdit
        ? api.put('/api/students', payload)
        : api.post<{ studentId: number }>('/api/students', payload)),
      isEdit ? 'Student updated.' : 'Student created.',
      (result) => {
        // A freshly created student goes straight into course enrolment, and saving that
        // hands off to their fees - the same three-step walk the legacy screens did.
        if (!isEdit) {
          navigate(`/students/${(result as { studentId: number }).studentId}/enroll`);
        }
      },
      (fieldErrors) => showErrors(form.setErrors, fieldErrors),
    );

    if (ok && isEdit) navigate('/students');
  }

  return (
    <FormShell
      title={isEdit ? `Edit student STD-${id}` : 'New student'}
      loading={formData.loading || existing.loading}
      error={formData.error ?? existing.error}
    >
      <FormGrid>
        <TextField id="name" label="Student name" value={v.name} onChange={form.setText('name')} required error={form.errors.name} />
        <TextField id="fatherName" label="Father name" value={v.fatherName} onChange={form.setText('fatherName')} required error={form.errors.fatherName} />
        <SelectField
          id="gradeId"
          label="Grade"
          required
          value={v.gradeId}
          onChange={form.setNumber('gradeId')}
          error={form.errors.gradeId}
          options={(formData.data?.grades ?? []).map((g) => ({ value: g.id, label: g.description }))}
        />
        <TextField id="schoolName" label="School name" value={v.schoolName} onChange={form.setText('schoolName')} error={form.errors.schoolName} />
        <TextField id="schoolBranch" label="School branch" value={v.schoolBranch} onChange={form.setText('schoolBranch')} error={form.errors.schoolBranch} />
        {/* The pattern is shown per field, because the server enforces it per field. Leave
            any of these blank and no rule applies at all. */}
        <TextField id="studentMobileNo" label="Student mobile" value={v.studentMobileNo} onChange={form.setText('studentMobileNo')} placeholder="92-3001234567" hint="Format 92-3######### · optional" error={form.errors.studentMobileNo} />
        <TextField id="parentMobileNo" label="Parent mobile" value={v.parentMobileNo} onChange={form.setText('parentMobileNo')} placeholder="92-3009876543" hint="Format 92-3######### · optional" error={form.errors.parentMobileNo} />
        <TextField id="homeLandline" label="Home landline" value={v.homeLandline} onChange={form.setText('homeLandline')} placeholder="021-12345678" hint="Format 0##-######## · optional" error={form.errors.homeLandline} />
        <TextField id="studentEmail" label="Student email" type="email" value={v.studentEmail} onChange={form.setText('studentEmail')} hint="Adding an email also creates their login." error={form.errors.studentEmail} />
        <TextField id="parentEmail" label="Parent email" type="email" value={v.parentEmail} onChange={form.setText('parentEmail')} error={form.errors.parentEmail} />
        {!isEdit && (
          <NumberField
            id="registrationFees"
            label="Registration fee"
            value={registrationFees}
            onChange={setRegistrationFees}
          />
        )}
      </FormGrid>

      <PhotoCapture
        value={base64Image}
        onChange={setBase64Image}
        existingPhotoPath={(existing.data as { photoPath?: string } | null)?.photoPath}
      />

      {/* Four related toggles in a bordered two-by-two block rather than a ragged stack:
          they read as one decision ("who hears about what"), so they are grouped as one. */}
      <fieldset className="checkgroup">
        <legend className="checkgroup__legend">Notifications</legend>

        <p className="checkgroup__note">
          These control which rows are queued. Sending is disabled at the client&rsquo;s request,
          so nothing is actually delivered.
        </p>

        <div className="checkgroup__grid">
          <CheckboxField id="isReportToStudent" label="Progress reports to student" checked={v.isReportToStudent} onChange={form.setBool('isReportToStudent')} />
          <CheckboxField id="isReportToParent" label="Progress reports to parent" checked={v.isReportToParent} onChange={form.setBool('isReportToParent')} />
          <CheckboxField id="isSendFeesReportToStudent" label="Fees reports to student" checked={v.isSendFeesReportToStudent} onChange={form.setBool('isSendFeesReportToStudent')} />
          <CheckboxField id="isSendFeesReportToParent" label="Fees reports to parent" checked={v.isSendFeesReportToParent} onChange={form.setBool('isSendFeesReportToParent')} />
        </div>
      </fieldset>

      <FormActions busy={busy} onSave={() => void save()} onCancel={() => navigate('/students')} />
    </FormShell>
  );
}

// ---------------------------------------------------------------------------
// Teacher
// ---------------------------------------------------------------------------

type TeacherValues = {
  id: number;
  name: string;
  address: string;
  mobileNo: string;
  cnic: string;
  email: string;
  association: string;
  qualification: string;
};

const emptyTeacher: TeacherValues = {
  id: 0, name: '', address: '', mobileNo: '', cnic: '', email: '', association: '', qualification: '',
};

export function TeacherFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const existing = useResource<TeacherValues & { teacherShare?: { sharePercent: number } }>(
    isEdit ? `/api/teachers/${id}` : null);

  const form = useForm<TeacherValues>(emptyTeacher);
  // Empty on create, exactly like the legacy form. It is required and range-checked below.
  const [sharePercent, setSharePercent] = useState('');

  useEffect(() => {
    if (!existing.data) return;
    form.setValues({ ...emptyTeacher, ...existing.data });
    if (existing.data.teacherShare) setSharePercent(String(existing.data.teacherShare.sharePercent));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data]);

  const v = form.values;

  async function save() {
    const problems = validatePerson(v);
    if (!v.association.trim()) problems.association = 'Association is required.';

    // Mirrors TeacherShare.SharePercent: a required int with [Range(1, 100)].
    const pct = sharePercent.trim();
    if (!pct) {
      problems.sharePercent = 'Share percent is required.';
    } else if (!/^\d+$/.test(pct) || Number(pct) < 1 || Number(pct) > 100) {
      problems.sharePercent = 'Share percent must be a whole number between 1 and 100.';
    }

    if (Object.keys(problems).length > 0) {
      showErrors(form.setErrors, problems);
      return;
    }

    form.setErrors({});

    // Qualification is the one optional field. Legacy MVC bound a blank text box to null
    // (ConvertEmptyStringToNull), so an empty qualification was stored as NULL and shown as
    // "—". Send null here too rather than "", so new rows read back the same way.
    const payload = {
      teacher: { ...v, qualification: orNull(v.qualification) },
      sharePercent: Number(pct),
    };

    const ok = await run(
      () => (isEdit ? api.put('/api/teachers', payload) : api.post('/api/teachers', payload)),
      isEdit ? 'Teacher updated.' : 'Teacher created.',
      undefined,
      (fieldErrors) => showErrors(form.setErrors, fieldErrors),
    );

    if (ok) navigate('/teachers');
  }

  return (
    <FormShell
      title={isEdit ? `Edit teacher T-${id}` : 'New teacher'}
      subtitle="Creating a teacher also opens their salary record for the current month."
      loading={existing.loading}
      error={existing.error}
    >
      <FormGrid>
        <TextField id="name" label="Name" value={v.name} onChange={form.setText('name')} required error={form.errors.name} />
        <TextField id="email" label="Email" type="email" value={v.email} onChange={form.setText('email')} required hint="A login account is created from this." error={form.errors.email} />
        <TextField id="mobileNo" label="Mobile" value={v.mobileNo} onChange={form.setText('mobileNo')} required placeholder="92-3001234567" error={form.errors.mobileNo} />
        <TextField id="cnic" label="CNIC" value={v.cnic} onChange={form.setText('cnic')} required placeholder="12345-1234567-1" error={form.errors.cnic} />
        <TextField id="address" label="Address" value={v.address} onChange={form.setText('address')} required error={form.errors.address} />
        <TextField id="association" label="Association" value={v.association} onChange={form.setText('association')} required error={form.errors.association} />
        <TextField id="qualification" label="Qualification" value={v.qualification} onChange={form.setText('qualification')} error={form.errors.qualification} />
        <NumberField
          id="sharePercent"
          label="Share percent"
          required
          value={sharePercent}
          onChange={(next) => {
            setSharePercent(next);
            form.clearError('sharePercent');
          }}
          error={form.errors.sharePercent}
          hint="Whole numbers only. Changing this affects future months; already-generated months keep their original percentage."
        />
      </FormGrid>

      <FormActions busy={busy} onSave={() => void save()} onCancel={() => navigate('/teachers')} />
    </FormShell>
  );
}

// ---------------------------------------------------------------------------
// Staff
// ---------------------------------------------------------------------------

type StaffValues = {
  id: number;
  name: string;
  address: string;
  mobileNo: string;
  cnic: string;
  email: string;
  designationId: number | '';
};

const emptyStaff: StaffValues = {
  id: 0, name: '', address: '', mobileNo: '', cnic: '', email: '', designationId: '',
};

export function StaffFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const formData = useResource<{ designations: Lookup[]; roles: string[] }>('/api/staff/form-data');
  const existing = useResource<StaffValues & { salary?: { amount: number } }>(
    isEdit ? `/api/staff/${id}` : null);

  const form = useForm<StaffValues>(emptyStaff);
  const [salaryAmount, setSalaryAmount] = useState('');
  const [role, setRole] = useState('');

  useEffect(() => {
    if (!existing.data) return;
    form.setValues({ ...emptyStaff, ...existing.data });
    if (existing.data.salary) setSalaryAmount(String(existing.data.salary.amount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data]);

  const v = form.values;

  async function save() {
    const problems = validatePerson(v);
    if (!v.designationId) problems.designationId = 'Designation is required.';

    // Legacy bound Staff.Salary.Amount (a non-nullable float) so a blank box failed
    // ModelState with "The Salary Amount field is required." Reproduced here.
    const sal = salaryAmount.trim();
    if (!sal) problems.salaryAmount = 'Monthly salary is required.';
    else if (Number.isNaN(Number(sal)) || Number(sal) < 0) {
      problems.salaryAmount = 'Enter a valid amount.';
    }

    if (Object.keys(problems).length > 0) {
      showErrors(form.setErrors, problems);
      return;
    }

    form.setErrors({});

    const payload = {
      staff: { ...v, designationId: Number(v.designationId) || 0 },
      salaryAmount: Number(sal),
      role: isEdit ? '' : role,
    };

    const ok = await run(
      () => (isEdit ? api.put('/api/staff', payload) : api.post('/api/staff', payload)),
      isEdit ? 'Staff member updated.' : 'Staff member created.',
      undefined,
      (fieldErrors) => showErrors(form.setErrors, fieldErrors),
    );

    if (ok) navigate('/staff');
  }

  return (
    <FormShell
      title={isEdit ? `Edit staff STF-${id}` : 'New staff member'}
      subtitle="A login is created only when both a role and an email are supplied."
      loading={formData.loading || existing.loading}
      error={formData.error ?? existing.error}
    >
      <FormGrid>
        <TextField id="name" label="Name" value={v.name} onChange={form.setText('name')} required error={form.errors.name} />
        <SelectField
          id="designationId"
          label="Designation"
          required
          value={v.designationId}
          onChange={form.setNumber('designationId')}
          error={form.errors.designationId}
          options={(formData.data?.designations ?? []).map((d) => ({ value: d.id, label: d.description }))}
        />
        <TextField id="email" label="Email" type="email" value={v.email} onChange={form.setText('email')} required error={form.errors.email} />
        <TextField id="mobileNo" label="Mobile" value={v.mobileNo} onChange={form.setText('mobileNo')} placeholder="92-3001234567" error={form.errors.mobileNo} />
        <TextField id="cnic" label="CNIC" value={v.cnic} onChange={form.setText('cnic')} placeholder="12345-1234567-1" error={form.errors.cnic} />
        <TextField id="address" label="Address" value={v.address} onChange={form.setText('address')} error={form.errors.address} />
        <NumberField
          id="salaryAmount"
          label="Monthly salary"
          required
          value={salaryAmount}
          onChange={(next) => { setSalaryAmount(next); form.clearError('salaryAmount'); }}
          error={form.errors.salaryAmount}
        />
        {!isEdit && (
          <SelectField
            id="role"
            label="Login role"
            value={role}
            onChange={setRole}
            options={(formData.data?.roles ?? []).map((r) => ({ value: r, label: r }))}
            placeholder="— no login —"
          />
        )}
      </FormGrid>

      <FormActions busy={busy} onSave={() => void save()} onCancel={() => navigate('/staff')} />
    </FormShell>
  );
}

// ---------------------------------------------------------------------------
// Course
// ---------------------------------------------------------------------------

type CourseValues = {
  id: number;
  gradeId: number | '';
  subjectId: number | '';
  teacherId: number | '';
  groupName: string;
  sessionStartYear: number | '';
  sessionEndYear: number | '';
};

const emptyCourse: CourseValues = {
  id: 0, gradeId: '', subjectId: '', teacherId: '', groupName: '',
  sessionStartYear: new Date().getFullYear(), sessionEndYear: new Date().getFullYear() + 1,
};

export function CourseFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const formData = useResource<{
    grades: Lookup[];
    subjects: Lookup[];
    teachers: { id: number; name: string }[];
  }>('/api/courses/form-data');

  const existing = useResource<CourseValues & { courseFees?: { amount: number } }>(
    isEdit ? `/api/courses/${id}` : null);

  const form = useForm<CourseValues>(emptyCourse);
  const [courseFees, setCourseFees] = useState('');

  useEffect(() => {
    if (!existing.data) return;
    form.setValues({ ...emptyCourse, ...existing.data });
    if (existing.data.courseFees) setCourseFees(String(existing.data.courseFees.amount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data]);

  const v = form.values;

  /**
   * The legacy Create/Edit relied on ModelState over the Course entity: the three lookups are
   * non-nullable ints behind a blank <option>, GroupName is [Required], both session years
   * carry [Range(2015, 2050)] and CourseFees.Amount [Range(0, 100000)]. All of that showed
   * inline in the old form; this reproduces it before the round trip.
   */
  function validate(): Record<string, string> {
    const e: Record<string, string> = {};

    if (!v.gradeId) e.gradeId = 'Grade is required.';
    if (!v.subjectId) e.subjectId = 'Subject is required.';
    if (!v.teacherId) e.teacherId = 'Teacher is required.';

    if (!v.groupName.trim()) e.groupName = 'Group name is required.';
    else if (v.groupName.trim().length > 100) e.groupName = 'Group name cannot exceed 100 characters.';

    for (const key of ['sessionStartYear', 'sessionEndYear'] as const) {
      const year = Number(v[key]);
      if (v[key] === '' || !Number.isInteger(year) || year < 2015 || year > 2050) {
        e[key] = 'Enter a year between 2015 and 2050.';
      }
    }

    const fee = Number(courseFees);
    if (courseFees.trim() === '' || Number.isNaN(fee) || fee < 0 || fee > 100000) {
      e.courseFees = 'Enter an amount between 0 and 100000.';
    }

    return e;
  }

  async function save() {
    const problems = validate();
    if (Object.keys(problems).length > 0) {
      showErrors(form.setErrors, problems);
      return;
    }
    form.setErrors({});

    const payload = {
      course: {
        ...v,
        gradeId: Number(v.gradeId) || 0,
        subjectId: Number(v.subjectId) || 0,
        teacherId: Number(v.teacherId) || 0,
        sessionStartYear: Number(v.sessionStartYear) || 0,
        sessionEndYear: Number(v.sessionEndYear) || 0,
      },
      courseFeesAmount: Number(courseFees) || 0,
    };

    const ok = await run(
      () => (isEdit ? api.put('/api/courses', payload) : api.post('/api/courses', payload)),
      isEdit ? 'Course updated.' : 'Course created.',
      undefined,
      (fieldErrors) => showErrors(form.setErrors, fieldErrors),
    );

    if (ok) navigate('/courses');
  }

  return (
    <FormShell
      title={isEdit ? `Edit course C-${id}` : 'New course'}
      subtitle="The course fee is copied onto a student when they enrol; changing it later affects new enrolments only."
      loading={formData.loading || existing.loading}
      error={formData.error ?? existing.error}
    >
      <FormGrid>
        <SelectField
          id="gradeId" label="Grade" required value={v.gradeId} onChange={form.setNumber('gradeId')}
          error={form.errors.gradeId}
          options={(formData.data?.grades ?? []).map((g) => ({ value: g.id, label: g.description }))}
        />
        <SelectField
          id="subjectId" label="Subject" required value={v.subjectId} onChange={form.setNumber('subjectId')}
          error={form.errors.subjectId}
          options={(formData.data?.subjects ?? []).map((s) => ({ value: s.id, label: s.description }))}
        />
        <SelectField
          id="teacherId" label="Teacher" required value={v.teacherId} onChange={form.setNumber('teacherId')}
          error={form.errors.teacherId}
          options={(formData.data?.teachers ?? []).map((t) => ({ value: t.id, label: `${t.name} (T-${t.id})` }))}
        />
        <TextField
          id="groupName" label="Group name" required maxLength={100}
          value={v.groupName} onChange={form.setText('groupName')} error={form.errors.groupName}
          hint="Maximum 100 characters, matching the legacy form."
        />
        <NumberField id="sessionStartYear" label="Session start year" required value={v.sessionStartYear} onChange={form.setNumber('sessionStartYear')} error={form.errors.sessionStartYear} />
        <NumberField id="sessionEndYear" label="Session end year" required value={v.sessionEndYear} onChange={form.setNumber('sessionEndYear')} error={form.errors.sessionEndYear} />
        <NumberField
          id="courseFees" label="Monthly fee" required
          value={courseFees}
          onChange={(next) => { setCourseFees(next); form.clearError('courseFees'); }}
          error={form.errors.courseFees}
        />
      </FormGrid>

      <FormActions busy={busy} onSave={() => void save()} onCancel={() => navigate('/courses')} />
    </FormShell>
  );
}

// ---------------------------------------------------------------------------
// Test
// ---------------------------------------------------------------------------

type TestValues = {
  id: number;
  courseId: number | '';
  topic: string;
  dateTime: string;
  totalMarks: number | '';
};

const emptyTest: TestValues = { id: 0, courseId: '', topic: '', dateTime: '', totalMarks: '' };

export function TestFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const formData = useResource<{
    courses: {
      id: number;
      groupName: string;
      grade?: { description: string };
      subject?: { description: string };
    }[];
  }>('/api/tests/form-data');

  const form = useForm<TestValues>(emptyTest);
  const v = form.values;

  /**
   * Legacy required a Course (non-nullable int, blank <option>) on create; the server checks
   * Topic and TotalMarks > 0. The course is fixed after creation, so it is only checked then.
   */
  function validate(): Record<string, string> {
    const e: Record<string, string> = {};

    if (!isEdit && !v.courseId) e.courseId = 'Course is required.';
    if (!v.topic.trim()) e.topic = 'Topic is required.';
    if (v.totalMarks === '' || Number(v.totalMarks) <= 0) {
      e.totalMarks = 'Total marks must be greater than 0.';
    }

    return e;
  }

  async function save() {
    const problems = validate();
    if (Object.keys(problems).length > 0) {
      showErrors(form.setErrors, problems);
      return;
    }
    form.setErrors({});

    const payload = {
      ...v,
      courseId: Number(v.courseId) || 0,
      totalMarks: Number(v.totalMarks) || 0,
      dateTime: v.dateTime || new Date().toISOString(),
    };

    const ok = await run(
      () => (isEdit ? api.put('/api/tests', payload) : api.post('/api/tests', payload)),
      isEdit ? 'Test updated.' : 'Test created.',
      undefined,
      (fieldErrors) => showErrors(form.setErrors, fieldErrors),
    );

    if (ok) navigate('/tests');
  }

  return (
    <FormShell
      title={isEdit ? `Edit test T-${id}` : 'New test'}
      subtitle="The course cannot be changed after creation — score rows are already attached to it."
      loading={formData.loading}
      error={formData.error}
    >
      <FormGrid>
        {!isEdit && (
          <SelectField
            id="courseId" label="Course" required value={v.courseId} onChange={form.setNumber('courseId')}
            error={form.errors.courseId}
            options={(formData.data?.courses ?? []).map((c) => ({
              value: c.id,
              label: `${c.grade?.description ?? ''} · ${c.subject?.description ?? ''} · ${c.groupName}`,
            }))}
          />
        )}
        <TextField id="topic" label="Topic" value={v.topic} onChange={form.setText('topic')} required error={form.errors.topic} />
        <TextField id="dateTime" label="Test date" type="date" value={v.dateTime?.slice(0, 10) ?? ''} onChange={form.setText('dateTime')} />
        <NumberField id="totalMarks" label="Total marks" value={v.totalMarks} onChange={form.setNumber('totalMarks')} required error={form.errors.totalMarks} />
      </FormGrid>

      <FormActions busy={busy} onSave={() => void save()} onCancel={() => navigate('/tests')} />
    </FormShell>
  );
}
